import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import ProgressBar from '@/components/ProgressBar';
import ReturnToDashboardPill from '@/components/ReturnToDashboardPill';
import PreparingScreen from '@/components/PreparingScreen';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';
import useTaskCompletion from '@/hooks/useTaskCompletion';
import useAutoGenerate from '@/hooks/useAutoGenerate';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { botRunsAPI, projectsAPI } from '@/utils/api';
import { setUser as setStoredUser } from '@/utils/auth';
import api from '@/utils/api';
import { toast } from 'sonner';
import { FloppyDisk } from '@phosphor-icons/react';
import MissionFinalizeActions from '@/components/MissionFinalizeActions';

const BusinessBuilderBotPage = () => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const { completeIfDeepLinked } = useTaskCompletion();
  const flags = useFeatureFlags();
  const [step, setStep] = useState('input');
  const [status, setStatus] = useState('pending');
  const [runId, setRunId] = useState(null);
  const [formData, setFormData] = useState({
    interests: '',
    time_per_week: '10-15 hours',
    preferred_platform: 'Instagram',
    monetization_preference: 'digital products',
    confidence_level: 'beginner',
  });
  const [outputs, setOutputs] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep('running');
    setStatus('running');

    try {
      const response = await botRunsAPI.create({ bot_type: 'business_builder', inputs: formData });
      setRunId(response.data.id);
      setOutputs(response.data.outputs);
      setStatus('completed');
      setTimeout(() => setStep('result'), 1000);
      toast.success('Business plan generated!');

      // Round 70/71 — the backend post-completion hook writes
      // business_memory + flips users.onboarding_completed=true on
      // every successful business_builder run. Refresh the cached
      // user so the dashboard gate evaluates correctly on the next
      // navigation. No URL flag, no auto-redirect — same UX as the
      // dashboard "Start Here" CTA; user navigates back when ready.
      try {
        const me = await api.get('/auth/me');
        if (me?.data) setStoredUser(me.data);
      } catch (_) { /* dashboard will refetch on next mount */ }

      // Round 215 (2026-08-02) — Onboarding Autopilot.
      // Kick off Market Intelligence + Opportunity Intelligence right
      // after the business plan is generated. Fire-and-forget: by the
      // time the customer scrolls to the dashboard MI/OI cards, both
      // documents are persisted and load instantly. Failures are
      // silent — the persisted-document endpoints auto-generate on
      // first read as a fallback.
      api.post('/market-intelligence/refresh').catch((err) =>
        console.info('[onboarding] MI kickoff skipped:', err?.message),
      );
      api.post('/opportunity-intelligence/refresh').catch((err) =>
        console.info('[onboarding] OI kickoff skipped:', err?.message),
      );
    } catch (error) {
      setStatus('failed');
      toast.error('Failed to generate business plan.');
    }
  };

  const handleSave = async () => {
    const projectName = prompt('Enter a name for this project:');
    if (!projectName) return;
    setSaving(true);
    try {
      const res = await projectsAPI.create({ project_name: projectName, bot_run_id: runId });
      toast.success('Saved to Projects!');
      // Round 96 — close the Round-94 workflow loop.
      await completeIfDeepLinked(
        { asset_id: res?.data?.id || runId, saved_at: new Date().toISOString() },
      );
    } catch (error) {
      toast.error('Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  // Round 95 — deep-link acceptance from <TaskActionButton />.
  useEffect(() => {
    if (deepLink.hasDeepLink) {
      console.info('[BusinessBuilder] deep-link accepted', deepLink);
    }
  }, [deepLink.hasDeepLink, deepLink.asset_id, deepLink.task_id]);

  // Post-Round-96 execution loop — invoke the business_builder
  // generator on arrival with existing business memory.
  //
  // Round 101 — prefer the exact mission context from the
  // Generate chip (`deepLink.mission`) so the builder continues
  // the exact business_profile / business_plan the customer
  // approved instead of regenerating from generic memory.
  const buildBuilderInputs = useCallback((memory) => {
    const m = deepLink.mission || {};
    const approved = m.approved_edits || {};
    return {
      niche: m.niche || memory.niche || memory.business_name || formData.niche || '',
      business_type: memory.business_type || formData.business_type || '',
      target_audience: m.target_audience || memory.target_audience || formData.target_audience || '',
      asset_focus: deepLink.asset_type || m.asset_type || 'business_plan',
      mission_context: (m && Object.keys(m).length) ? {
        asset_id: m.asset_id,
        asset_type: m.asset_type,
        mission_id: m.mission_id,
        mission_title: m.mission_title,
        // Round 104 — approved edits win.
        approved_summary: approved.summary,
        approved_key_points: approved.key_points,
        publishing_instructions: approved.publishing_instructions
          || m.publishing_instructions,
      } : undefined,
      business_context: {
        business_name: m.business_name || memory.business_name || '',
        primary_offer: memory.primary_offer || '',
        pricing: memory.pricing || '',
      },
    };
  }, [
    formData.niche, formData.business_type, formData.target_audience,
    deepLink.asset_type, deepLink.mission,
  ]);

  const handleAutoResult = useCallback(async (resp) => {
    setRunId(resp?.data?.id);
    setOutputs(resp?.data?.outputs);
    setStatus('completed');
    setStep('result');
    toast.success(
      "Great work! Today\u2019s next business step is ready.",
      { description: 'Review, refine, and approve when you\u2019re happy.' },
    );
    // Refresh cached user (same as manual save path).
    try {
      const me = await api.get('/auth/me');
      if (me?.data) setStoredUser(me.data);
    } catch (_) { /* noop */ }
  }, []);

  const auto = useAutoGenerate({
    botType: 'business_builder',
    buildInputs: buildBuilderInputs,
    onResult: handleAutoResult,
    enabled: Boolean(deepLink.mission) || Boolean(flags.execution_loop_v1),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-4 flex items-center justify-between">
          <ReturnToDashboardPill testId="business-bot-return-pill" />
          {deepLink.hasDeepLink && (
            <span
              className="text-xs text-slate-500"
              data-testid="business-bot-deep-link-indicator"
              data-workflow-task-id={deepLink.task_id || ''}
              data-workflow-asset-id={deepLink.asset_id || ''}
              data-workflow-mode={deepLink.mode || ''}
              data-workflow-asset-type={deepLink.asset_type || ''}
            >
              Deep-linked from your Dashboard task
            </span>
          )}
        </div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-testid="business-bot-title">Business Builder Bot</h1>
          <p className="text-slate-600">Get a complete business launch roadmap and strategy</p>
        </div>

        {/* Round 102 — Mission continuation panel. Shown whenever
             the customer arrived via a Today's Mission Generate
             chip so they never see the generic Business Builder
             form as a fallback. */}
        {deepLink.mission && (step === 'input' || auto.isPreparing) && !outputs && (
          <div
            className="mb-6 rounded-xl border border-blue-200 bg-white shadow-sm p-6"
            data-testid="business-bot-mission-continuation"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 mb-1">
                  Continuing your mission
                </p>
                <h2
                  className="text-xl font-semibold text-slate-900 truncate"
                  data-testid="business-bot-mission-title"
                >
                  {deepLink.mission.mission_title || 'Today\u2019s mission'}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  {deepLink.mission.business_name || ''}
                  {deepLink.mission.niche ? ` \u00b7 ${deepLink.mission.niche}` : ''}
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wider">
                {deepLink.mission.asset_type || 'business'}
              </span>
            </div>
            {auto.isPreparing && (
              <p className="mt-4 text-xs text-slate-500 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                Generating from your approved mission&hellip;
              </p>
            )}
          </div>
        )}

        {auto.isPreparing && !deepLink.mission && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <PreparingScreen
              assetType={auto.assetKind || 'business_plan'}
              testId="business-bot-preparing"
            />
          </div>
        )}

        {!auto.isPreparing && step === 'input' && !deepLink.mission && (
          <form onSubmit={handleSubmit} data-testid="business-bot-form" className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your Interests *</label>
              <input type="text" name="interests" required data-testid="business-interests-input" value={formData.interests} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., online business, coaching, content creation" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Time Available Per Week *</label>
              <select name="time_per_week" data-testid="business-time-select" value={formData.time_per_week} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="5-10 hours">5-10 hours</option>
                <option value="10-15 hours">10-15 hours</option>
                <option value="15-20 hours">15-20 hours</option>
                <option value="20+ hours">20+ hours</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Platform *</label>
              <select name="preferred_platform" data-testid="business-platform-select" value={formData.preferred_platform} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="Instagram">Instagram</option>
                <option value="TikTok">TikTok</option>
                <option value="YouTube">YouTube</option>
                <option value="Blog">Blog</option>
                <option value="Multiple">Multiple Platforms</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Monetization Preference *</label>
              <select name="monetization_preference" data-testid="business-monetization-select" value={formData.monetization_preference} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="digital products">Digital Products</option>
                <option value="coaching">Coaching</option>
                <option value="services">Services</option>
                <option value="affiliate">Affiliate Marketing</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confidence Level *</label>
              <select name="confidence_level" data-testid="business-confidence-select" value={formData.confidence_level} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
              </select>
            </div>
            <button type="submit" data-testid="business-bot-submit" className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">Build My Business Plan</button>
          </form>
        )}

        {step === 'running' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6" data-testid="business-bot-running">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Building Your Business Roadmap...</h3>
            </div>
            <ProgressBar status={status} />
          </div>
        )}

        {step === 'result' && outputs && (
          <div className="space-y-6" data-testid="business-bot-results">
            {/* Round 104 — Approve & Generate finalize + return bar */}
            <MissionFinalizeActions
              outputs={outputs}
              botRunId={runId}
              projectName={(deepLink.mission && deepLink.mission.mission_title) || 'Business Mission'}
              buildMetaPatch={(o) => ({
                summary: o?.business_model || o?.niche_recommendation || undefined,
                key_points: Array.isArray(o?.first_actions) && o.first_actions.length
                  ? o.first_actions : undefined,
              })}
              renderAsText={(o) => {
                const parts = [];
                if (o?.business_model) parts.push('BUSINESS MODEL\n' + o.business_model);
                if (o?.niche_recommendation) parts.push('NICHE\n' + o.niche_recommendation);
                if (o?.content_strategy) parts.push('CONTENT STRATEGY\n' + o.content_strategy);
                if (o?.monetization_plan) parts.push('MONETIZATION\n' + o.monetization_plan);
                if (Array.isArray(o?.launch_roadmap) && o.launch_roadmap.length) {
                  const weeks = o.launch_roadmap.map((w) => {
                    const tasks = Array.isArray(w?.tasks) ? w.tasks.join('\n  • ') : '';
                    return `Week ${w.week}: ${w.focus}\n  • ${tasks}`;
                  });
                  parts.push('30-DAY ROADMAP\n' + weeks.join('\n\n'));
                }
                if (Array.isArray(o?.first_actions) && o.first_actions.length) {
                  parts.push('FIRST 5 ACTIONS\n' + o.first_actions.map((a, i) => `${i + 1}. ${a}`).join('\n'));
                }
                return parts.join('\n\n');
              }}
              filenamePrefix="paygbot-business"
              testIdPrefix="business-bot-mission-actions"
            />

            <div className="flex flex-wrap gap-4">
              {!deepLink.hasDeepLink && (
                <button onClick={handleSave} disabled={saving} data-testid="save-project-button" className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                  <FloppyDisk size={20} weight="duotone" />
                  <span>{saving ? 'Saving...' : 'Save to Projects'}</span>
                </button>
              )}
              <button onClick={() => { setStep('input'); setStatus('pending'); setOutputs(null); }} data-testid="run-another-button" className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-medium border border-slate-200 rounded-lg">Run Another</button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Business Model</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{outputs.business_model}</pre>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Niche Recommendation</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{outputs.niche_recommendation}</pre>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">30-Day Launch Roadmap</h3>
              <div className="space-y-4">
                {outputs.launch_roadmap.map((week, i) => (
                  <div key={i} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Week {week.week}: {week.focus}</h4>
                    <ul className="space-y-1">
                      {week.tasks.map((task, j) => (
                        <li key={j} className="text-sm text-blue-800 flex items-start space-x-2">
                          <span>•</span>
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Content Strategy</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{outputs.content_strategy}</pre>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Monetization Plan</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{outputs.monetization_plan}</pre>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 space-y-4">
              <h3 className="text-xl font-semibold text-emerald-900">Your First 5 Actions</h3>
              <ul className="space-y-2">
                {outputs.first_actions.map((action, i) => (
                  <li key={i} className="text-sm text-emerald-800">{action}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessBuilderBotPage;